import React, { useState, useEffect } from "react";
import {
  useReference,
  useScalpProfiles,
  useCreateScalpProfile,
  useUpdateScalpProfile,
  useDeleteScalpProfile,
  useScalpAttempts,
  useClearAttempts,
  useTestFireScalp,
  useAccounts,
  usePayments,
  useResolveCaptcha,
  useCancelAttempt,
  useProxies,
  useShippingProfiles,
} from "@/lib/queries";
import { toast } from "sonner";
import { Crosshair, Plus, Trash2, Power, Zap, AlertTriangle, ShoppingCart, ExternalLink, CheckCircle2, XCircle, ShieldAlert, ShieldQuestion, Globe2, Ban, ListFilter, Radio, ChevronDown, ChevronRight, Pencil, X, Activity } from "lucide-react";
import { formatPrice, formatRelative, RETAILER_COLORS, api } from "@/lib/cr";
import SmartActionBuilder from "@/components/SmartActionBuilder";

const SELECT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308]";
const INPUT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]";
const LABEL = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";

// Live "what the bot is doing right now" panel. Polls /api/scanner/activity
// every 5 seconds and shows the last 12 scrape events.
function ScannerActivityPanel() {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const { data } = await api.get("/scanner/activity?limit=12");
        if (!cancelled) { setEvents(data || []); setError(""); }
      } catch (e) {
        if (!cancelled) setError(e?.message || "fetch failed");
      }
    }
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return (
    <div data-testid="scanner-activity-panel" className="mt-4 bg-[#0a0a0a] border border-[#1f1f1f] rounded-sm">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1f1f1f]">
        <Activity className="w-3.5 h-3.5 text-[#22C55E] animate-pulse" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#22C55E]">Scanner activity — live</span>
        <span className="text-[10px] text-[#525252] ml-auto">{events.length} of last 12 events · refreshes every 5s</span>
      </div>
      {error && <div className="px-3 py-2 text-xs text-[#EF4444]">{error}</div>}
      {!error && events.length === 0 && (
        <div className="px-3 py-3 text-xs text-[#737373] italic">
          No scrape events yet — the scanner hasn&apos;t run since launch. Wait ~20 seconds for the first cycle.
        </div>
      )}
      {events.length > 0 && (
        <div className="max-h-[180px] overflow-y-auto font-mono text-[11px]">
          {events.map((e, i) => {
            const stockOk = e.in_stock_count > 0;
            const errored = !!e.error;
            const diagNote = e.diag && e.diag.note;
            const showNote = !errored && e.listings_count === 0 && diagNote;
            return (
              <div key={i} className="px-3 py-1.5 border-b border-[#1A1A1A] hover:bg-[#0d0d0d]">
                <div className="flex items-center gap-2">
                  <span className="text-[#525252] shrink-0 w-16">{e.ts ? new Date(e.ts).toLocaleTimeString().split(" ")[0] : ""}</span>
                  <span className="text-white shrink-0 w-32 truncate" title={e.retailer}>{e.retailer}</span>
                  <span className="text-[#EAB308] shrink-0 truncate" title={e.query}>&quot;{e.query}&quot;</span>
                  <span className="text-[#A3A3A3] ml-auto shrink-0">
                    {errored ? (
                      <span className="text-[#EF4444]">err: {e.error.slice(0, 40)}</span>
                    ) : (
                      <>
                        <span className="text-[#737373]">{e.listings_count} found</span>
                        <span className={`ml-2 ${stockOk ? "text-[#22C55E]" : "text-[#525252]"}`}>
                          {e.in_stock_count} in stock
                        </span>
                      </>
                    )}
                  </span>
                </div>
                {showNote && (
                  <div className="text-[10px] text-[#737373] pl-[72px] mt-0.5 italic" title={JSON.stringify(e.diag)}>
                    why: {diagNote}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const EMPTY = {
  name: "",
  active: true,
  trigger: "product_monitor",
  filters: [
    { field: "game", op: "equals", value: "Pokemon" },
    { field: "price", op: "lte", value: 100 },
  ],
  actions: [
    { type: "notify_discord", config: {} },
  ],
  // Direct URL Watch — power-user feature, paste exact PDP URLs to monitor
  watched_urls: [],
  // Legacy scalar fields (still consumed by the executor)
  quantity: 1,
  allow_preorder: false,
  checkout_mode: "account",
  store_account_id: "",
  shipping_profile_id: "",
  payment_method_id: "",
  proxy_strategy: "any",
  proxy_id: "",
  max_total_spend: "",
  shipping_address: "",
};

const STATUS_STYLE = {
  queued: "text-[#A3A3A3] border-[#262626] bg-[#1A1A1A]",
  logging_in: "text-[#3B82F6] border-[#3B82F6]/30 bg-[#3B82F6]/10",
  inspecting_product: "text-[#3B82F6] border-[#3B82F6]/30 bg-[#3B82F6]/10",
  adding_to_cart: "text-[#EAB308] border-[#EAB308]/30 bg-[#EAB308]/10",
  preordering: "text-[#A855F7] border-[#A855F7]/30 bg-[#A855F7]/10",
  checking_out: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/10",
  captcha_pending: "text-[#A855F7] border-[#A855F7]/40 bg-[#A855F7]/10",
  resuming: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/10",
  success: "text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/10",
  failed: "text-[#EF4444] border-[#EF4444]/30 bg-[#EF4444]/10",
  blocked: "text-[#EF4444] border-[#EF4444]/30 bg-[#EF4444]/10",
  preorder_only: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/10",
  no_buy_button: "text-[#EF4444] border-[#EF4444]/30 bg-[#EF4444]/10",
  skipped: "text-[#737373] border-[#404040] bg-[#1A1A1A]",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLE[status] || STATUS_STYLE.queued;
  return (
    <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border rounded-sm uppercase ${cls}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

export default function ScalpPage() {
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);  // when set, form submits PATCH instead of POST
  const [expandedAttempt, setExpandedAttempt] = useState(null);  // attempt-id whose detail row is open
  const { data: ref } = useReference();
  const { data: profiles = [] } = useScalpProfiles();
  const { data: attempts = [] } = useScalpAttempts(60);
  const { data: accounts = [] } = useAccounts();
  const { data: payments = [] } = usePayments();
  const { data: proxies = [] } = useProxies();
  const { data: shippingProfiles = [] } = useShippingProfiles();
  const create = useCreateScalpProfile();
  const update = useUpdateScalpProfile();
  const del = useDeleteScalpProfile();
  const clearAttempts = useClearAttempts();
  const testFire = useTestFireScalp();
  const resolveCaptcha = useResolveCaptcha();
  const cancelAttempt = useCancelAttempt();

  const captchaPending = attempts.filter((a) => a.status === "captcha_pending");

  const sets = form.game && ref?.sets_by_game?.[form.game]
    ? ref.sets_by_game[form.game]
    : Object.values(ref?.sets_by_game || {}).flat();

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Profile name required");
      return;
    }
    const needsBuy = (form.actions || []).some((a) => a.type === "auto_buy");
    if (needsBuy && !form.payment_method_id) {
      toast.error("Auto-buy needs a payment method", { description: "Pick one inside the Auto-buy action card" });
      return;
    }
    if (needsBuy && form.checkout_mode === "account" && !form.store_account_id) {
      toast.error("Account checkout needs a store account", { description: "Pick one or switch to Guest checkout" });
      return;
    }
    const payload = {
      name: form.name,
      active: form.active,
      trigger: form.trigger || "product_monitor",
      filters: form.filters || [],
      actions: form.actions || [],
      checkout_mode: form.checkout_mode || "account",
      shipping_profile_id: form.shipping_profile_id || null,
      watched_urls: Array.isArray(form.watched_urls) ? form.watched_urls.filter(Boolean) : [],
      // Legacy scalar fields the executor still consumes
      max_total_spend: form.max_total_spend === "" ? null : Number(form.max_total_spend),
      quantity: Number(form.quantity) || 1,
      allow_preorder: !!form.allow_preorder,
      store_account_id: form.store_account_id || null,
      payment_method_id: form.payment_method_id || null,
      proxy_strategy: form.proxy_strategy || "any",
      proxy_id: form.proxy_strategy === "specific" ? (form.proxy_id || null) : null,
      shipping_address: form.shipping_address || null,
    };
    create.mutate(payload, {
      onSuccess: () => {
        toast.success("Smart Action armed");
        setForm(EMPTY);
      },
      onError: () => toast.error("Failed to create profile"),
    });
  };

  // Load an existing profile into the form for editing.
  const startEditing = (p) => {
    setEditingId(p.id);
    setForm({
      ...EMPTY,
      ...p,
      // Coerce optionals back to "" so the form inputs render correctly
      max_total_spend: p.max_total_spend ?? "",
      filters: p.filters || [],
      actions: p.actions || [],
    });
    // Scroll the form into view on small screens
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  // Override submit when in edit mode
  const submitWithEdit = (e) => {
    if (!editingId) return submit(e);
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Profile name required"); return; }
    const payload = {
      name: form.name,
      active: form.active,
      trigger: form.trigger || "product_monitor",
      filters: form.filters || [],
      actions: form.actions || [],
      checkout_mode: form.checkout_mode || "account",
      shipping_profile_id: form.shipping_profile_id || null,
      watched_urls: Array.isArray(form.watched_urls) ? form.watched_urls.filter(Boolean) : [],
      max_total_spend: form.max_total_spend === "" ? null : Number(form.max_total_spend),
      quantity: Number(form.quantity) || 1,
      allow_preorder: !!form.allow_preorder,
      store_account_id: form.store_account_id || null,
      payment_method_id: form.payment_method_id || null,
      proxy_strategy: form.proxy_strategy || "any",
      proxy_id: form.proxy_strategy === "specific" ? (form.proxy_id || null) : null,
      shipping_address: form.shipping_address || null,
    };
    update.mutate({ id: editingId, body: payload }, {
      onSuccess: () => {
        toast.success("Profile updated");
        cancelEditing();
      },
      onError: (e) => toast.error("Update failed", { description: e?.response?.data?.detail }),
    });
  };

  const toggleArray = (key, value) => {
    const arr = form[key];
    setForm({ ...form, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] });
  };

  return (
    <div className="p-6 max-w-7xl w-full mx-auto" data-testid="scalp-page">
      <header className="mb-4">
        <div className="flex items-center gap-3">
          <Crosshair className="w-7 h-7 text-[#EF4444]" />
          <h1 className="font-heading text-4xl tracking-tighter font-black uppercase">
            Scalp <span className="text-[#EF4444]">mode</span>
          </h1>
        </div>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Armed profiles auto-fire purchases the moment a matching listing drops.
        </p>
        <ScannerActivityPanel />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="scalp-pipeline-explainer">
          <div className="bg-[#A855F7]/5 border border-[#A855F7]/30 p-3 rounded-sm flex gap-2">
            <div className="w-6 h-6 rounded-sm bg-[#A855F7]/20 border border-[#A855F7]/40 flex items-center justify-center font-mono text-xs text-[#A855F7] font-bold shrink-0">1</div>
            <div className="text-[11px] leading-relaxed">
              <div className="text-white font-bold">Trigger</div>
              <div className="text-[#A3A3A3]">Scanner polls retailers every ~15 sec. Every new in-stock listing flows into your filters.</div>
            </div>
          </div>
          <div className="bg-[#EAB308]/5 border border-[#EAB308]/30 p-3 rounded-sm flex gap-2">
            <div className="w-6 h-6 rounded-sm bg-[#EAB308]/20 border border-[#EAB308]/40 flex items-center justify-center font-mono text-xs text-[#EAB308] font-bold shrink-0">2</div>
            <div className="text-[11px] leading-relaxed">
              <div className="text-white font-bold">Filters</div>
              <div className="text-[#A3A3A3]">All filters must match (AND). Set them to game, price ceiling, retailer, deal score — anything narrows the firehose.</div>
            </div>
          </div>
          <div className="bg-[#22C55E]/5 border border-[#22C55E]/30 p-3 rounded-sm flex gap-2">
            <div className="w-6 h-6 rounded-sm bg-[#22C55E]/20 border border-[#22C55E]/40 flex items-center justify-center font-mono text-xs text-[#22C55E] font-bold shrink-0">3</div>
            <div className="text-[11px] leading-relaxed">
              <div className="text-white font-bold">Actions</div>
              <div className="text-[#A3A3A3]">Run in order. Auto-buy (in .exe) handles the full checkout, or just ping you via Discord/Telegram.</div>
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2 text-[11px] text-[#EF4444] bg-[#EF4444]/5 border border-[#EF4444]/30 p-3 rounded-sm">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="text-[#FCA5A5]">
            <strong className="text-white">Cloud preview = MOCKED.</strong> Auto-buy actions only place real orders when running the local <code className="text-white">.exe</code> (with <code className="text-white">CARDRADAR_USE_REAL_ADAPTERS=1</code>). On this hosted preview, attempts are simulated and logged but no real charges happen.
          </div>
        </div>
      </header>

      {/* CAPTCHA-pending banner */}
      {captchaPending.length > 0 && (
        <div
          className="mt-4 bg-[#A855F7]/10 border border-[#A855F7]/40 p-4 rounded-sm"
          data-testid="captcha-pending-banner"
        >
          <div className="flex items-start gap-3">
            <ShieldQuestion className="w-5 h-5 text-[#A855F7] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold uppercase tracking-wider text-sm text-white">
                {captchaPending.length} attempt(s) blocked by CAPTCHA
              </div>
              <div className="text-xs text-[#A3A3A3] mt-1">
                Configure 2captcha in Settings to auto-solve, or resolve each one manually here.
              </div>
              <div className="mt-3 space-y-1.5">
                {captchaPending.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`captcha-pending-${a.id}`}
                    className="flex items-center justify-between gap-3 bg-[#0d0d0d] border border-[#A855F7]/20 px-3 py-2 rounded-sm"
                  >
                    <div className="text-xs text-white truncate flex-1">{a.listing_title}</div>
                    <div className="flex gap-1">
                      <button
                        data-testid={`resolve-captcha-${a.id}`}
                        onClick={() =>
                          resolveCaptcha.mutate(a.id, {
                            onSuccess: () => toast.success("CAPTCHA resolved — resuming"),
                          })
                        }
                        className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold bg-[#A855F7] text-white rounded-sm hover:bg-[#C084FC] flex items-center gap-1"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Resolved
                      </button>
                      <button
                        data-testid={`cancel-attempt-${a.id}`}
                        onClick={() =>
                          cancelAttempt.mutate(a.id, {
                            onSuccess: () => toast.success("Attempt cancelled"),
                          })
                        }
                        className="px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-sm flex items-center gap-1"
                      >
                        <Ban className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6 mt-6">
        {editingId && (
          <div className="bg-[#1E3A8A]/15 border border-[#3B82F6]/40 px-4 py-2.5 rounded-sm flex items-center justify-between gap-3" data-testid="scalp-editing-banner">
            <div className="text-[12px] text-[#93C5FD]">
              <strong className="text-white">Editing profile</strong> — changes save with the &ldquo;Update profile&rdquo; button. Arm/disarm/spending stats are unaffected.
            </div>
            <button
              type="button"
              onClick={cancelEditing}
              data-testid="scalp-cancel-edit"
              className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 border border-[#3B82F6]/40 text-[#93C5FD] hover:bg-[#3B82F6]/10 rounded-sm flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        )}
        {/* Profile form */}
        {/* Smart Action builder (3-column Trigger → Filters → Actions) */}
        <SmartActionBuilder
          form={form}
          setForm={setForm}
          onSubmit={submitWithEdit}
          accounts={accounts}
          payments={payments}
          proxies={proxies}
          shippingProfiles={shippingProfiles}
          reference={ref}
          saving={editingId ? update.isPending : create.isPending}
          submitLabel={editingId ? "Update profile" : null}
        />

        {/* Profile list */}
        <div className="space-y-3">
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider">
            Armed profiles ({profiles.length})
          </h2>
          {profiles.length === 0 ? (
            <div className="border border-dashed border-[#262626] bg-[#0a0a0a] p-8 text-center text-[#737373] rounded-sm text-sm">
              No scalp profiles yet.
            </div>
          ) : (
            profiles.map((p) => {
              const successRate = p.attempts_count > 0 ? Math.round((p.success_count / p.attempts_count) * 100) : 0;
              return (
                <div
                  key={p.id}
                  data-testid={`scalp-profile-${p.id}`}
                  className={`border p-4 rounded-sm card-hover ${p.active ? "bg-[#0d0d0d] border-[#EF4444]/40" : "bg-[#0a0a0a] border-[#262626]"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`pulse-dot ${p.active ? "" : "idle"}`} style={p.active ? { background: "#EF4444", boxShadow: "0 0 8px rgba(239,68,68,0.6)" } : {}} />
                        <div className="text-sm font-bold text-white truncate">{p.name}</div>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-mono text-[#737373]">
                        {p.game && <span>{p.game}</span>}
                        {p.set_name && <span>{p.set_name}</span>}
                        {p.product_type && <span>{p.product_type}</span>}
                        {p.max_unit_price && <span className="text-[#EAB308]">≤ {formatPrice(p.max_unit_price)}/unit</span>}
                        <span>qty {p.quantity}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {p.retailers.map((r) => (
                          <span key={r} className="text-[10px] font-mono uppercase px-1.5 py-0.5 border border-[#262626] text-[#A3A3A3] rounded-sm">
                            {r}
                          </span>
                        ))}
                        {p.regions.map((r) => (
                          <span key={r} className="text-[10px] font-mono uppercase px-1.5 py-0.5 border border-[#EF4444]/30 text-[#EF4444] rounded-sm">
                            {r}
                          </span>
                        ))}
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-3 text-[10px] font-mono">
                        <div className="text-[#737373]">Attempts<br /><span className="text-white text-sm">{p.attempts_count}</span></div>
                        <div className="text-[#737373]">Success<br /><span className="text-[#22C55E] text-sm">{p.success_count} ({successRate}%)</span></div>
                        <div className="text-[#737373]">Spent (sim)<br /><span className="text-white text-sm">{formatPrice(p.spent_total)}</span></div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        data-testid={`scalp-toggle-${p.id}`}
                        onClick={() =>
                          update.mutate({ id: p.id, body: { active: !p.active } }, {
                            onSuccess: () => toast.success(p.active ? "Disarmed" : "Armed"),
                          })
                        }
                        className={`p-2 rounded-sm transition-colors ${p.active ? "text-[#EF4444] hover:bg-[#EF4444]/10" : "text-[#737373] hover:text-white hover:bg-[#1A1A1A]"}`}
                        title={p.active ? "Disarm" : "Arm"}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`scalp-test-fire-${p.id}`}
                        onClick={() =>
                          testFire.mutate(p.id, {
                            onSuccess: () => toast.success("Test fire dispatched"),
                            onError: (e) => toast.error("Test fire failed", { description: e?.response?.data?.detail }),
                          })
                        }
                        className="p-2 text-[#737373] hover:text-[#EAB308] hover:bg-[#1A1A1A] rounded-sm transition-colors"
                        title="Force one attempt against a matching in-stock listing"
                      >
                        <Zap className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`scalp-edit-${p.id}`}
                        onClick={() => startEditing(p)}
                        className={`p-2 rounded-sm transition-colors ${editingId === p.id ? "text-[#3B82F6] bg-[#3B82F6]/10" : "text-[#737373] hover:text-[#3B82F6] hover:bg-[#1A1A1A]"}`}
                        title="Edit this profile (works while armed)"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`scalp-delete-${p.id}`}
                        onClick={() => del.mutate(p.id, { onSuccess: () => toast.success("Profile removed") })}
                        className="p-2 text-[#737373] hover:text-[#EF4444] hover:bg-[#1A1A1A] rounded-sm transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Attempts feed */}
      <section className="mt-8" data-testid="attempts-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-[#EAB308]" />
            Purchase attempts ({attempts.length})
          </h2>
          {attempts.length > 0 && (
            <button
              data-testid="clear-attempts-btn"
              onClick={() => clearAttempts.mutate(undefined, { onSuccess: () => toast.success("Attempts cleared") })}
              className="px-3 py-1.5 text-xs uppercase font-bold tracking-wider border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 flex items-center gap-1.5 rounded-sm"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear log
            </button>
          )}
        </div>

        {attempts.length === 0 ? (
          <div className="border border-dashed border-[#262626] bg-[#0a0a0a] p-10 text-center text-[#737373] rounded-sm text-sm">
            No attempts yet. Arm a profile and start the scanner — attempts will stream in here.
          </div>
        ) : (
          <div className="border border-[#262626] rounded-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="attempts-table">
              <thead className="bg-[#0d0d0d] border-b border-[#262626]">
                <tr className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">
                  <th className="w-8 px-2 py-2.5"></th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5">When</th>
                  <th className="text-left px-3 py-2.5">Item</th>
                  <th className="text-left px-3 py-2.5">Retailer</th>
                  <th className="text-left px-3 py-2.5">Proxy</th>
                  <th className="text-right px-3 py-2.5">Qty</th>
                  <th className="text-right px-3 py-2.5">Total</th>
                  <th className="text-left px-3 py-2.5">Order / Error</th>
                  <th className="text-left px-3 py-2.5">Profile</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => {
                  const color = RETAILER_COLORS[a.retailer_id] || "#A3A3A3";
                  const isOpen = expandedAttempt === a.id;
                  const stepLogs = a.step_logs || [];
                  return (
                    <React.Fragment key={a.id}>
                    <tr
                      data-testid={`attempt-row-${a.id}`}
                      onClick={() => setExpandedAttempt(isOpen ? null : a.id)}
                      className="border-b border-[#1A1A1A] hover:bg-[#0d0d0d] transition-colors cursor-pointer"
                    >
                      <td className="px-2 py-2.5 text-center">
                        {isOpen
                          ? <ChevronDown className="w-3.5 h-3.5 text-[#EAB308] inline" />
                          : <ChevronRight className="w-3.5 h-3.5 text-[#525252] inline" />}
                      </td>
                      <td className="px-3 py-2.5"><StatusBadge status={a.status} /></td>
                      <td className="px-3 py-2.5 text-xs font-mono text-[#A3A3A3]">{formatRelative(a.started_at)}</td>
                      <td className="px-3 py-2.5 text-sm text-white truncate max-w-xs">{a.listing_title}</td>
                      <td className="px-3 py-2.5">
                        <span className="text-[10px] font-mono font-bold px-2 py-0.5 border rounded-sm"
                          style={{ color, borderColor: `${color}55`, backgroundColor: "#0a0a0a" }}>
                          {a.retailer_name}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        {a.proxy_label ? (
                          <span className="font-mono text-[#A855F7] flex items-center gap-1">
                            <Globe2 className="w-3 h-3" />{a.proxy_label}
                          </span>
                        ) : (
                          <span className="text-[#525252] font-mono">direct</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-white">{a.quantity}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-white">{formatPrice(a.total, a.currency)}</td>
                      <td className="px-3 py-2.5 text-xs">
                        {a.status === "success" ? (
                          <span className={`font-mono flex items-center gap-1 ${String(a.order_ref || "").startsWith("MOCK-") ? "text-[#A855F7]" : "text-[#22C55E]"}`}>
                            <CheckCircle2 className="w-3 h-3" />{a.order_ref}
                          </span>
                        ) : a.error ? (
                          <span className="text-[#EF4444] font-mono flex items-center gap-1">
                            <XCircle className="w-3 h-3" />{a.error}
                          </span>
                        ) : a.status === "captcha_pending" ? (
                          <span className="text-[#A855F7] font-mono flex items-center gap-1">
                            <ShieldQuestion className="w-3 h-3" />waiting
                          </span>
                        ) : (
                          <span className="text-[#737373] font-mono">…</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-[#A3A3A3] truncate max-w-[150px]">{a.profile_name}</td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`attempt-detail-${a.id}`} className="border-b border-[#1A1A1A] bg-[#050505]">
                        <td></td>
                        <td colSpan={9} className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            <div>
                              <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1">Attempt ID</div>
                              <div className="text-xs font-mono text-[#D4D4D4] break-all">{a.id}</div>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1">Order reference</div>
                              <div className="text-xs font-mono text-white break-all">
                                {a.order_ref || <span className="text-[#525252]">(none)</span>}
                                {String(a.order_ref || "").startsWith("MOCK-") && (
                                  <span className="ml-2 text-[9px] uppercase tracking-wider text-[#A855F7] border border-[#A855F7]/50 px-1.5 py-0.5 rounded-sm">SIMULATED — not real</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1">Finished at</div>
                              <div className="text-xs font-mono text-[#D4D4D4]">{a.finished_at || <span className="text-[#525252]">in progress</span>}</div>
                            </div>
                          </div>
                          {a.error && (
                            <div className="mb-3 px-3 py-2 bg-[#7F1D1D]/15 border border-[#DC2626]/40 rounded-sm">
                              <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#FCA5A5] mb-1">Error code</div>
                              <div className="text-xs font-mono text-[#FCA5A5]">{a.error}</div>
                            </div>
                          )}
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-2">Step-by-step log</div>
                            {stepLogs.length === 0 ? (
                              <div className="text-xs text-[#525252] italic">(no step logs captured)</div>
                            ) : (
                              <div className="space-y-1">
                                {stepLogs.map((step, i) => (
                                  <div key={i} className="flex items-start gap-3 text-xs font-mono">
                                    <span className="text-[#525252] shrink-0">{step.ts ? new Date(step.ts).toLocaleTimeString() : ""}</span>
                                    <span
                                      className="shrink-0 uppercase tracking-wider font-bold w-20"
                                      style={{ color: step.status === "success" ? "#22C55E"
                                                : step.status === "skipped" ? "#A855F7"
                                                : step.status === "error" || step.status === "failed" ? "#EF4444"
                                                : "#EAB308" }}
                                    >{step.status}</span>
                                    <span className="text-[#D4D4D4] flex-1">{step.msg}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
